import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { doc, setDoc, serverTimestamp, getDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

/**
 * Complete Profile Page
 * 
 * This page is shown to users who signed up via OAuth (Apple, Google)
 * and need to complete their profile with account type, trade, etc.
 */
const CompleteProfile = () => {
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checkingProfile, setCheckingProfile] = useState(true);
  const [fullName, setFullName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [accountMode, setAccountMode] = useState<string>("personal");
  const [userType, setUserType] = useState<string>("professional");
  const [tradeType, setTradeType] = useState<string>("");
  const [customTradeType, setCustomTradeType] = useState<string>("");
  const navigate = useNavigate();
  const { toast } = useToast();

  // Check if user already has a complete profile
  useEffect(() => {
    const checkProfile = async () => {
      if (!user) {
        setCheckingProfile(false);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          // If profile is complete (has tradeType), redirect to dashboard
          if (data.tradeType && data.tradeType !== 'pending') {
            navigate("/");
            return;
          }
          // Pre-fill name from OAuth
          if (data.fullName) {
            setFullName(data.fullName);
          }
        }
        
        // Also try to get name from Firebase Auth
        if (user.displayName && !fullName) {
          setFullName(user.displayName);
        }
      } catch (error) {
        console.error("Error checking profile:", error);
      } finally {
        setCheckingProfile(false);
      }
    };

    if (!authLoading) {
      checkProfile();
    }
  }, [user, authLoading, navigate]);

  // Redirect if not logged in - but give time for auth state to propagate
  // This is important for OAuth sign-ins where navigation happens before auth state updates
  useEffect(() => {
    console.log('🔍 CompleteProfile auth check:', { authLoading, hasUser: !!user, userId: user?.uid });
    
    // Don't redirect while still loading
    if (authLoading) {
      console.log('⏳ CompleteProfile: Still loading auth...');
      return;
    }
    
    // If we have a user, we're good
    if (user) {
      console.log('✅ CompleteProfile: User found:', user.uid);
      return;
    }
    
    // Give a longer delay to allow auth state to propagate from the sign-in
    // This handles the race condition where we navigate before onAuthStateChanged fires
    console.log('⏳ CompleteProfile: No user yet, waiting 2s for auth to settle...');
    const timer = setTimeout(() => {
      if (!user) {
        console.log('⚠️ CompleteProfile: No user after delay, redirecting to auth');
        navigate("/auth");
      }
    }, 2000); // Wait 2 seconds for auth state to settle
    
    return () => clearTimeout(timer);
  }, [user, authLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to complete your profile",
        variant: "destructive",
      });
      return;
    }

    if (!tradeType) {
      toast({
        title: "Required Field",
        description: "Please select your business industry",
        variant: "destructive",
      });
      return;
    }

    if (tradeType === "other" && !customTradeType.trim()) {
      toast({
        title: "Required Field",
        description: "Please specify your business industry",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      // Map trade type
      const tradeMap: Record<string, string> = {
        'electrician': 'electrician',
        'plumber': 'plumber',
        'framer': 'framer',
        'hvac': 'hvac',
        'gas': 'gas_installer',
        'gas_installer': 'gas_installer',
        'roofing': 'roofing',
        'general': 'general',
        'other': 'electrician', // Default for custom trades
      };
      const finalTradeType = tradeMap[tradeType] || 'electrician';

      // Determine roles based on account mode
      const roles: string[] = [];
      if (accountMode === 'business' || accountMode === 'enterprise') {
        roles.push('admin');
      }

      // Update user profile
      await setDoc(doc(db, "users", user.uid), {
        fullName: fullName.trim(),
        companyName: companyName.trim() || null,
        accountMode: accountMode,
        tradeType: finalTradeType,
        customTradeType: tradeType === "other" ? customTradeType.trim() : null,
        roles: roles,
        profileCompleted: true,
        updatedAt: serverTimestamp(),
      }, { merge: true });

      toast({
        title: "Profile Complete!",
        description: "Welcome to Normiz. Let's get started!",
      });

      navigate("/");
    } catch (error: any) {
      console.error("Error completing profile:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to complete profile",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || checkingProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-background to-secondary/10">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-background to-secondary/10 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-2">
          <div className="flex justify-center mb-2">
            <img src="/icons/normiz-favicon.svg" alt="Normiz" className="h-20 w-20" />
          </div>
          <CardTitle className="text-2xl">Complete Your Profile</CardTitle>
          <CardDescription>
            Just a few more details to get you started
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fullName">Full Name</Label>
              <Input
                id="fullName"
                type="text"
                placeholder="John Doe"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="companyName">Company Name (Optional)</Label>
              <Input
                id="companyName"
                type="text"
                placeholder="ABC Construction"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="accountMode">Account Type</Label>
              <Select value={accountMode} onValueChange={setAccountMode}>
                <SelectTrigger id="accountMode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="personal">Personal - Perfect for solo professionals</SelectItem>
                  <SelectItem value="business">Business - For teams (charged per user)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {accountMode === "business" && (
              <div className="space-y-2">
                <Label htmlFor="userType">I am a...</Label>
                <Select value={userType} onValueChange={setUserType}>
                  <SelectTrigger id="userType">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="professional">Service Professional</SelectItem>
                    <SelectItem value="general_contractor">General Contractor</SelectItem>
                    <SelectItem value="construction_firm">Construction Firm</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {accountMode === "personal" && (
              <div className="space-y-2">
                <Label>Account Type</Label>
                <div className="px-3 py-2 border border-input rounded-md bg-muted/50 text-sm">
                  Service Professional (Solo)
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="tradeType">Business Industry</Label>
              <Select value={tradeType} onValueChange={setTradeType} required>
                <SelectTrigger id="tradeType">
                  <SelectValue placeholder="Select your industry" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="electrician">Electrical</SelectItem>
                  <SelectItem value="plumber">Plumbing</SelectItem>
                  <SelectItem value="hvac">HVAC</SelectItem>
                  <SelectItem value="framer">Framing/Carpentry</SelectItem>
                  <SelectItem value="gas">Gas Installation</SelectItem>
                  <SelectItem value="roofing">Roofing</SelectItem>
                  <SelectItem value="general">General Contractor</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {tradeType === "other" && (
              <div className="space-y-2">
                <Label htmlFor="customTradeType">Specify Your Industry</Label>
                <Input
                  id="customTradeType"
                  type="text"
                  placeholder="e.g., Landscaping, Roofing..."
                  value={customTradeType}
                  onChange={(e) => setCustomTradeType(e.target.value)}
                  required
                />
              </div>
            )}

            <Button type="submit" className="w-full" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Complete Setup
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CompleteProfile;
